/*
 * Koleksiyon içi arama (snippets/hp-collection-search.liquid)
 *
 * 1. İlk aramada sections/hp-collection-search-index.liquid'den koleksiyonun hafif ürün listesi
 *    (id, başlık, stok, sıra) aktif filtre ve sıralamayla çekilir. Eşleştirme bellekte yapılır,
 *    bu yüzden yazdıkça sonuçlar anında değişir.
 * 2. Kartlar grid'in kendi section'ından (Section Rendering API) alınır; sadece eşleşen ürünlerin
 *    bulunduğu sayfalar çekilir ve cache'lenir. Böylece kartlar normal grid ile birebir aynıdır.
 * 3. Sonuçlar grid'in sayfa boyutu kadar parçalar halinde basılır, kalanı kaydırdıkça eklenir.
 * 4. Arama temizlenince grid'in orijinal içeriği (kartlar + sayfalama) aynen geri konur.
 *
 * Filtre/sıralama değiştiğinde component-facets.js "collection:rendered" event'i gönderir;
 * arama yeni grid'e tekrar uygulanır ve "X ürün gösteriliyor" sayacı güncellenir.
 */

if (!customElements.get('hp-collection-search')) {
  (() => {
    const INDEX_SECTION_ID = 'hp-collection-search-index';
    const INDEX_PAGE_SIZE = 250;
    const INPUT_DELAY = 120;
    const MAX_CACHED_STATES = 5;

    const locales = window.hpCollectionSearchLocales || {};

    // Büyük/küçük harf ve Türkçe karakter duyarsız: "ÇAY", "çay" ve "cay" aynı sonucu verir.
    const normalize = (value) =>
      value
        .toLocaleLowerCase('tr')
        .replace(/ı/g, 'i')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['’`]/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();

    const tokenize = (value) => normalize(value).split(' ').filter(Boolean);

    // Grid içeriğini belirleyen parametreler (filtre, sıralama, vendor/type sayfalarındaki q).
    // utm vb. parametreler isteklere taşınmaz.
    const getStateKey = () => {
      const params = new URLSearchParams();
      new URLSearchParams(window.location.search).forEach((value, key) => {
        if (key.startsWith('filter.') || key === 'sort_by' || key === 'q' || key === 'options[prefix]') {
          params.append(key, value);
        }
      });
      return params.toString();
    };

    const buildUrl = (sectionId, stateKey, page) => {
      const params = new URLSearchParams(stateKey);
      params.set('section_id', sectionId);
      params.set('page', page);
      return `${window.location.pathname}?${params.toString()}`;
    };

    const fetchText = async (url) => {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`${response.status} ${url}`);
      return response.text();
    };

    let cardUid = 0;

    // Başka bir sayfadan çekilen kartı, grid'e eklenmeye hazırlar.
    const prepareCard = (card) => {
      // Quick-buy ID'leri sayfadaki sıraya göre üretiliyor (örn. quick_buy-<section>-2), yani farklı
      // sayfalardaki kartlarda aynı. product-variants fiyat ve formu document.querySelector ile bulduğu
      // için ID'ler çakışırsa başka kartın formuna bağlanır. Kart içindeki bütün referanslar benzersiz yapılır.
      const suffix = `hp${++cardUid}`;
      card.querySelectorAll('product-variants[data-id]').forEach((variants) => {
        const oldId = variants.dataset.id;
        if (!oldId) return;
        const newId = `${oldId}-${suffix}`;
        [card, ...card.querySelectorAll('*')].forEach((element) => {
          for (const attribute of element.attributes) {
            if (attribute.value.includes(oldId)) attribute.value = attribute.value.split(oldId).join(newId);
          }
        });
      });

      // Section Rendering ile gelen HTML'deki inline script'ler çalışmaz. Quick-buy'ın script'le yaptığı
      // işi component-facets.js'teki renderProductGrid ile aynı şekilde burada yapıyoruz.
      card.querySelectorAll('template').forEach((template) => {
        template.closest('form')?.append(template.content.cloneNode(true));
      });

      return card;
    };

    class CollectionSearch {
      constructor() {
        this.term = '';
        this.tokens = [];
        this.elements = new Set();
        this.states = new Map();
        this.view = null;
        this.runId = 0;
        this.timer = null;
        this.countText = '';

        this.sentinel = document.createElement('div');
        this.sentinel.className = 'hp-collection-search__sentinel';
        this.sentinel.setAttribute('aria-hidden', 'true');
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.renderNextBatch();
          },
          { rootMargin: '0px 0px 800px 0px' }
        );

        document.addEventListener('collection:rendered', () => this.onGridRendered());
        document.addEventListener('shopify:section:load', () => this.onSectionLoad());

        this.syncView();
        this.updateCount();
      }

      createView(grid) {
        const nodes = new Map();
        grid.querySelectorAll(':scope > [data-js-product-item]').forEach((card) => {
          nodes.set(card.id.replace('product-item-', ''), card);
        });

        return {
          grid,
          key: getStateKey(),
          sectionId: grid.dataset.id,
          perPage: Number(grid.dataset.perPage),
          productsCount: Number(grid.dataset.productsCount) || 0,
          nodes, // ürün id -> kart; mevcut sayfanın kartları + sonradan çekilenler
          originals: null, // arama açıkken grid'in orijinal içeriği
          matches: [],
          missing: 0,
          rendered: 0,
          busy: 0,
        };
      }

      syncView() {
        const grid = document.getElementById('main-collection-product-grid');
        if (grid && this.view?.grid !== grid) this.view = this.createView(grid);
        return grid ? this.view : null;
      }

      getState(key) {
        const state = this.states.get(key) || { index: null, pages: new Map() };
        this.states.delete(key);
        this.states.set(key, state);
        if (this.states.size > MAX_CACHED_STATES) this.states.delete(this.states.keys().next().value);
        return state;
      }

      register(element) {
        this.elements.add(element);
        element.sync(this.term);
      }

      setTerm(value, source = null) {
        this.term = value;
        this.elements.forEach((element) => {
          if (element.isConnected) element.sync(value, source);
          else this.elements.delete(element);
        });

        const tokens = tokenize(value);
        if (tokens.join(' ') === this.tokens.join(' ')) return;
        this.tokens = tokens;

        clearTimeout(this.timer);
        this.timer = null;
        if (tokens.length) this.timer = setTimeout(() => this.run(), INPUT_DELAY);
        else this.run();
      }

      flush() {
        if (this.timer) this.run();
      }

      prefetch() {
        const view = this.syncView();
        if (view) this.getIndex(view).catch(() => {});
      }

      async run() {
        clearTimeout(this.timer);
        this.timer = null;

        const view = this.syncView();
        if (!view) return;

        const runId = ++this.runId;
        this.observer.disconnect();

        if (!this.tokens.length) {
          this.restore(view);
          return;
        }

        if (!view.originals) view.originals = Array.from(view.grid.childNodes);
        view.grid.classList.add('loading');

        let index;
        try {
          index = await this.getIndex(view);
        } catch (error) {
          if (runId === this.runId) this.showError(view, error);
          return;
        }
        if (runId !== this.runId) return;

        const tokens = this.tokens;
        view.matches = index.filter((item) => tokens.every((token) => item.text.includes(token)));
        view.missing = 0;
        view.rendered = 0;
        await this.renderBatch(view, runId);
      }

      renderNextBatch() {
        const view = this.view;
        if (!view || !view.originals || view.busy || view.rendered >= view.matches.length) return;
        this.renderBatch(view, this.runId);
      }

      async renderBatch(view, runId) {
        const batch = view.matches.slice(view.rendered, view.rendered + view.perPage);
        view.busy = runId;

        try {
          await this.loadCards(view, batch);
        } catch (error) {
          if (view.busy === runId) view.busy = 0;
          if (runId === this.runId) this.showError(view, error);
          return;
        }

        if (view.busy === runId) view.busy = 0;
        if (runId !== this.runId || view !== this.view) return;

        const fragment = document.createDocumentFragment();
        batch.forEach((item) => {
          const card = view.nodes.get(item.id);
          if (card) fragment.append(card);
          else view.missing += 1;
        });

        const isFirstBatch = view.rendered === 0;
        view.rendered += batch.length;

        if (isFirstBatch) {
          view.grid.replaceChildren(fragment);
          if (!view.grid.children.length) {
            view.grid.append(this.createMessage((locales.noResults || '').replace('[terms]', this.term.trim())));
          }
          view.grid.classList.remove('loading');
        } else {
          view.grid.append(fragment);
        }

        if (view.rendered < view.matches.length) {
          // Gözlem yeniden başlatılır; sentinel hâlâ görünürse sıradaki parça hemen yüklenir.
          view.grid.append(this.sentinel);
          this.observer.observe(this.sentinel);
        } else {
          this.sentinel.remove();
        }

        this.updateCount();
      }

      async loadCards(view, items) {
        const pages = new Set();
        items.forEach((item) => {
          if (!view.nodes.has(item.id)) pages.add(Math.floor(item.pos / view.perPage) + 1);
        });
        await Promise.all([...pages].map((page) => this.loadPage(view, page)));
      }

      async loadPage(view, page) {
        const state = this.getState(view.key);

        if (!state.pages.has(page)) {
          const request = fetchText(buildUrl(view.sectionId, view.key, page)).then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const cards = doc.querySelectorAll('#main-collection-product-grid > [data-js-product-item]');
            // adoptNode: kartlar ana dokümana taşınır, ayrıştırılan doküman bellekte tutulmaz.
            // Custom element'ler (quick-buy, css-slider...) kart grid'e eklendiğinde kurulur.
            return Array.from(cards, (card) => prepareCard(document.adoptNode(card)));
          });
          request.catch(() => state.pages.delete(page));
          state.pages.set(page, request);
        }

        const cards = await state.pages.get(page);
        cards.forEach((card) => {
          const id = card.id.replace('product-item-', '');
          if (!view.nodes.has(id)) view.nodes.set(id, card);
        });
      }

      getIndex(view) {
        const state = this.getState(view.key);
        if (!state.index) {
          state.index = this.loadIndex(view).catch((error) => {
            state.index = null;
            throw error;
          });
        }
        return state.index;
      }

      async loadIndex(view) {
        const loadIndexPage = async (page) => {
          const html = await fetchText(buildUrl(INDEX_SECTION_ID, view.key, page));
          const match = html.match(/<script[^>]*data-hp-collection-search-index[^>]*>([\s\S]*?)<\/script>/);
          if (!match) throw new Error('hp-collection-search: index not found');
          return JSON.parse(match[1]);
        };

        // Sayfa sayısı grid'deki filtrelenmiş ürün sayısından tahmin edilir ve hepsi paralel istenir.
        const estimatedPages = Math.max(1, Math.ceil(view.productsCount / INDEX_PAGE_SIZE));
        const pages = await Promise.all(Array.from({ length: estimatedPages }, (_, i) => loadIndexPage(i + 1)));
        const extraPages = [];
        for (let page = estimatedPages + 1; page <= pages[0].pages; page++) extraPages.push(loadIndexPage(page));
        pages.push(...(await Promise.all(extraPages)));

        const seen = new Set();
        const items = [];
        pages.forEach((page) => {
          page.items.forEach(([id, title, available, pos]) => {
            const key = String(id);
            if (seen.has(key)) return;
            seen.add(key);
            items.push({ id: key, text: normalize(title), available: available === 1, pos });
          });
        });

        // Grid'deki gibi önce stokta olanlar, sonra stokta olmayanlar (kendi aralarında mevcut sıralamayla).
        return [...items.filter((item) => item.available), ...items.filter((item) => !item.available)];
      }

      restore(view) {
        if (view.originals) {
          view.grid.replaceChildren(...view.originals);
          view.originals = null;
          view.grid.classList.remove('loading');
        }
        view.matches = [];
        view.missing = 0;
        view.rendered = 0;
        this.updateCount();
      }

      showError(view, error) {
        console.error('[hp-collection-search]', error);
        view.matches = [];
        view.rendered = 0;
        view.grid.replaceChildren(this.createMessage(locales.error || ''));
        view.grid.classList.remove('loading');
        this.setCountText('');
      }

      createMessage(text) {
        const message = document.createElement('p');
        message.className = 'no-content-message hp-collection-search__message';
        message.textContent = text;
        return message;
      }

      // Filtre/sıralama değişti, grid yeniden çizildi (component-facets.js).
      onGridRendered() {
        const previous = this.view;
        const view = this.syncView();

        if (!view || view === previous || !this.tokens.length) {
          this.updateCount();
          return;
        }

        // Yeni sonuçlar gelene kadar önceki arama sonuçları soluk gösterilir, filtrelenmemiş grid görünmez.
        view.originals = Array.from(view.grid.childNodes);
        if (previous?.originals) view.grid.replaceChildren(...previous.grid.childNodes);
        view.grid.classList.add('loading');
        this.setCountText(this.countText);
        this.run();
      }

      // Tema editöründe section yeniden yüklendiğinde arama sıfırlanır.
      onSectionLoad() {
        if (document.getElementById('main-collection-product-grid') === this.view?.grid) return;
        this.observer.disconnect();
        this.runId += 1;
        this.view = null;
        this.setTerm('');
        this.syncView();
        this.updateCount();
      }

      updateCount() {
        const view = this.view;
        if (!view) return;
        const count = view.originals
          ? view.matches.length - view.missing
          : view.grid.querySelectorAll(':scope > [data-js-product-item]').length;
        this.setCountText((locales.count || '[count]').replace('[count]', count));
      }

      setCountText(text) {
        this.countText = text;
        document.querySelectorAll('.js-data-counts').forEach((element) => {
          element.textContent = text;
        });
      }
    }

    const controller = new CollectionSearch();

    class HPCollectionSearch extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('[data-hp-collection-search-input]');
        this.clearButton = this.querySelector('[data-hp-collection-search-clear]');

        // Masaüstünde kutu filtre formunun içinde; input event'i forma ulaşırsa filtre isteği tetiklenir.
        this.addEventListener('input', (event) => {
          event.stopPropagation();
          controller.setTerm(this.input.value, this);
        });

        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            // Enter filtre formunu submit edip sayfayı yenilemesin
            event.preventDefault();
            controller.flush();
            if (window.matchMedia('(hover: none)').matches) this.input.blur();
          } else if (event.key === 'Escape' && this.input.value) {
            event.preventDefault();
            controller.setTerm('');
          }
        });

        this.input.addEventListener('focus', () => controller.prefetch());

        this.clearButton.addEventListener('click', () => {
          controller.setTerm('');
          this.input.focus();
        });

        controller.register(this);
      }

      sync(value, source) {
        if (source !== this && this.input.value !== value) this.input.value = value;
        this.clearButton.hidden = !value;
      }
    }

    customElements.define('hp-collection-search', HPCollectionSearch);
  })();
}
